/**
 * Converts a time string "HH:mm" to total minutes since midnight.
 * e.g., "14:30" -> (14 * 60) + 30 = 870
 */
const timeStringToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return (hours * 60) + minutes;
};

const applyDoWDefaults = (days) => {

    let ret = {};

    ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].forEach((day) => {
        ret[day] = !!days[day]
    });

    // console.log(ret)
    return ret;
}

function formatToDateUTCString(date_str, timeZone) {
    if (!date_str) return '';

    const [year, month, day] = date_str.split('-').map(Number);

    // Month is 0-indexed in JavaScript (Jan = 0, May = 4)
    const pureMidnightUTC = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    return pureMidnightUTC;
}

function formatToDateTimeUTCString(date_str, timeZone) {
    if (!date_str) return '';

    const [datePart, timePart] = date_str.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    // Create a temporary UTC date using the local numbers
    const utcBase = new Date(Date.UTC(year, month - 1, day, hour, minute));

    // Check what time it *actually* is in that timezone if it were this UTC time
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });

    const parts = formatter.formatToParts(utcBase);
    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
    const tzBase = new Date(Date.UTC(map.year, map.month - 1, map.day, map.hour, map.minute));

    // Calculate the offset difference and apply it to find the real UTC time
    const diff = utcBase.getTime() - tzBase.getTime();

    //   console.log(new Date(utcBase.getTime() + diff).toISOString())
    return new Date(utcBase.getTime() + diff).toISOString();
}

/**
 * Formats the react-hook-form data to match the Mongoose schema requirements.
 */
export const formatCampaignDataForSubmit = (formData, timeZone) => {
    // Create a deep copy so we don't mutate the original form state
    const payload = JSON.parse(JSON.stringify(formData));
    const type = payload.scheduleType;

    if (payload.content && payload.content._id) {
        payload.contentId = payload.content._id;

        // Clean up: prevent sending the preview URL and extra data to MongoDB
        delete payload.content;
    }

    // Clean up scheduling object based on the selected scheduleType
    // We only keep and format the relevant array to avoid saving unused data
    const formattedScheduling = {
        startEnd: [],
        recurring: [],
        recurringWithDate: []
    };

    if (type === "startEnd" && payload.scheduling?.startEnd) {
        formattedScheduling.startEnd = payload.scheduling.startEnd.map(slot => ({
            // Convert "YYYY-MM-DDThh:mm" to a standard Date object
            startTime: formatToDateTimeUTCString(slot.startTime, timeZone),
            endTime: formatToDateTimeUTCString(slot.endTime, timeZone)
        }));
    }

    if (type === "recurring" && payload.scheduling?.recurring) {
        formattedScheduling.recurring = payload.scheduling.recurring.map(slot => ({
            days: applyDoWDefaults(slot.days || {}),
            // Convert "HH:mm" string to Number (minutes)
            startTime: timeStringToMinutes(slot.startTime),
            endTime: timeStringToMinutes(slot.endTime)
        }));
    }

    if (type === "recurringWithDate" && payload.scheduling?.recurringWithDate) {
        formattedScheduling.recurringWithDate = payload.scheduling.recurringWithDate.map(slot => ({
            days: applyDoWDefaults(slot.days || {}),
            // Convert "YYYY-MM-DD" string to Number (Unix Timestamp in milliseconds)
            startDate: formatToDateUTCString(slot.startDate, timeZone).getTime(),
            endDate: formatToDateUTCString(slot.endDate, timeZone).getTime(),
            // Convert "HH:mm" string to Number (minutes)
            startTime: timeStringToMinutes(slot.startTime),
            endTime: timeStringToMinutes(slot.endTime)
        }));
    }

    payload.scheduling = formattedScheduling;

    return payload;
};

/**
 * Converts total minutes since midnight back to "HH:mm".
 * e.g., 870 -> "14:30"
 */
const minutesToTimeString = (totalMinutes) => {
    if (typeof totalMinutes !== 'number') return "00:00";
    const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
    const minutes = (totalMinutes % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

/**
 * Converts a Unix timestamp (Number) or Date string to "YYYY-MM-DD" for type="date"
 */
const timestampToDateString = (dateInput, timeZone) => {
    const date = new Date(dateInput);
    
    // Extract coordinates directly from the UTC timeline
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');

    // Returns exactly "YYYY-MM-DD"
    return `${year}-${month}-${day}`;
};

/**
 * Converts a Date string to "YYYY-MM-DDTHH:mm" for type="datetime-local"
 */
const dateToDatetimeLocalString = (dateInput) => {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Converts a Date string to "YYYY-MM-DDTHH:mm" for type="datetime-local", but as the organisation's timezone date
 */
const dateToDatetimeLocalTimezoneString = (dateInput, timeZone) => {
    if (!dateInput) return "";

    const date = new Date(dateInput);
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });

    const parts = formatter.formatToParts(date);
    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));

    const new_date = `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
    // console.log('dateToDatetimeLocalTimezoneString', new_date)

    return new_date;
};

/**
 * Formats the raw Mongoose document so react-hook-form inputs can read it.
 */
export const formatCampaignDataForForm = (apiData, timeZone) => {
    // Deep clone to avoid mutating the original fetched data
    const data = JSON.parse(JSON.stringify(apiData));

    if (!data.scheduling) return data;

    const type = data.scheduleType;

    if (type === "startEnd" && data.scheduling.startEnd) {
        data.scheduling.startEnd = data.scheduling.startEnd.map(slot => ({
            ...slot,
            startTime: dateToDatetimeLocalTimezoneString(slot.startTime, timeZone),
            endTime: dateToDatetimeLocalTimezoneString(slot.endTime, timeZone)
        }));
    }

    if (type === "recurring" && data.scheduling.recurring) {
        data.scheduling.recurring = data.scheduling.recurring.map(slot => ({
            ...slot,
            startTime: minutesToTimeString(slot.startTime),
            endTime: minutesToTimeString(slot.endTime)
        }));
    }

    if (type === "recurringWithDate" && data.scheduling.recurringWithDate) {
        data.scheduling.recurringWithDate = data.scheduling.recurringWithDate.map(slot => ({
            ...slot,
            startDate: timestampToDateString(slot.startDate, timeZone),
            endDate: timestampToDateString(slot.endDate, timeZone),
            startTime: minutesToTimeString(slot.startTime),
            endTime: minutesToTimeString(slot.endTime)
        }));
    }

    return data;
};